'use client';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Autocomplete, Snackbar, TextField } from '@mui/material';
import Header from '../../components/Header';
import Load from '../../components/Load';
import {
  LIGA_POR_ID,
  LigaPorIdData,
  LigaPorIdVars,
  atualizarLiga,
  uploadImagemLiga,
  uploadBannerLiga,
  atualizarImagemLiga,
  atualizarBannerLiga,
  LigaInput,
} from '../../services/ligaService';
import { buscarUsuarios, type Usuario } from '../../services/usuarioService';
import {
  organizadoresPorLiga,
  atualizarOrganizador,
  type Organizador,
} from '../../services/organizadorService';
import { dispatchGlobalError } from '../../context/ApiErrorContext';
import LeagueOrganizersTab from '../../components/liga/LeagueOrganizersTab';
import ConfigPagamentosTab from '../../components/liga/ConfigPagamentosTab';
import AdminLigaPaymentsTab from './liga/AdminLigaPaymentsTab';
import { PageBreadcrumb } from '../../components/navigation';

const adminEditLeagueSchema = z.object({
  nome: z.string().nonempty('Nome é obrigatório.'),
  username: z
    .string()
    .nonempty('Username é obrigatório.')
    .regex(/^[a-zA-Z0-9_-]+$/, 'Username pode conter apenas letras, números, underscore (_) e hífen (-).'),
  imagemUrl: z.string().optional(),
  bannerUrl: z.string().optional(),
  enderecoCompleto: z.string().optional(),
  ramoAtividade: z.string().optional(),
});

type AdminEditLeagueFormValues = z.infer<typeof adminEditLeagueSchema>;

const AdminEditLeague: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, loading, error, refetch } = useQuery<LigaPorIdData, LigaPorIdVars>(LIGA_POR_ID, {
    variables: { id: id! },
    skip: !id,
  });

  const liga = data?.ligaPorId;

  const {
    register,
    handleSubmit: handleFormSubmit,
    reset,
    watch,
    setValue,
    formState: { errors: formErrors },
  } = useForm<AdminEditLeagueFormValues>({
    resolver: zodResolver(adminEditLeagueSchema),
    defaultValues: {
      nome: '',
      username: '',
      imagemUrl: '',
      bannerUrl: '',
      enderecoCompleto: '',
      ramoAtividade: '',
    },
  });

  const [selectedAvatarFile, setSelectedAvatarFile] = useState<File | null>(null);
  const [previewAvatarUrl, setPreviewAvatarUrl] = useState<string | null>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  const [selectedBannerFile, setSelectedBannerFile] = useState<File | null>(null);
  const [previewBannerUrl, setPreviewBannerUrl] = useState<string | null>(null);
  const [isUploadingBanner, setIsUploadingBanner] = useState(false);

  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [avatarUploaded, setAvatarUploaded] = useState(false);
  const [bannerUploaded, setBannerUploaded] = useState(false);
  const [activeTab, setActiveTab] = useState('informacoes');

  // Organizer state
  const [selectedUser, setSelectedUser] = useState<Usuario | null>(null);
  const [userOptions, setUserOptions] = useState<Usuario[]>([]);
  const [userInputValue, setUserInputValue] = useState('');
  const [userLoading, setUserLoading] = useState(false);
  const [currentOrganizer, setCurrentOrganizer] = useState<Organizador | null>(null);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const handleCloseSnackbar = () =>
    setSnackbar((s) => ({ ...s, open: false }));

  // Preencher formulário quando liga for carregada
  useEffect(() => {
    if (liga) {
      reset({
        nome: liga.nome || '',
        username: liga.username || '',
        imagemUrl: liga.imagemUrl || '',
        bannerUrl: liga.bannerUrl || '',
        enderecoCompleto: liga.enderecoCompleto || '',
        ramoAtividade: liga.ramoAtividade || '',
      });
      setAvatarUploaded(false);
      setBannerUploaded(false);
      loadCurrentOrganizer();
    }
  }, [liga]);

  const loadCurrentOrganizer = async () => {
    if (!id) return;
    try {
      const organizers = await organizadoresPorLiga(id);
      const admin = organizers.find((org) => org.nomeFuncao === 'ADMINISTRADOR');
      if (admin) {
        setCurrentOrganizer(admin);
        if (admin.usuario) {
          setSelectedUser(admin.usuario as Usuario);
        }
      } else {
        setCurrentOrganizer(null);
      }
    } catch (err) {
      console.error('Erro ao carregar organizador:', err);
    }
  };

  // Cleanup de URLs de preview ao desmontar componente
  useEffect(() => {
    return () => {
      if (previewAvatarUrl) {
        URL.revokeObjectURL(previewAvatarUrl);
      }
      if (previewBannerUrl) {
        URL.revokeObjectURL(previewBannerUrl);
      }
    };
  }, []);

  // Cleanup de URL antiga quando uma nova for criada
  useEffect(() => {
    return () => {
      if (previewAvatarUrl) {
        URL.revokeObjectURL(previewAvatarUrl);
      }
    };
  }, [previewAvatarUrl]);

  useEffect(() => {
    return () => {
      if (previewBannerUrl) {
        URL.revokeObjectURL(previewBannerUrl);
      }
    };
  }, [previewBannerUrl]);

  // Busca de usuários para autocomplete
  useEffect(() => {
    if (!userInputValue.trim()) {
      setUserOptions([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        setUserLoading(true);
        const users = await buscarUsuarios(userInputValue);
        setUserOptions(users.filter((u) => u.ativo));
      } catch (err) {
        console.error('Erro ao buscar usuários:', err);
      } finally {
        setUserLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [userInputValue]);

  const handleAvatarFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      setSelectedAvatarFile(null);
      if (previewAvatarUrl) {
        URL.revokeObjectURL(previewAvatarUrl);
        setPreviewAvatarUrl(null);
      }
      return;
    }
    if (!file.type.startsWith('image/')) {
      dispatchGlobalError('Apenas arquivos de imagem são permitidos.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      dispatchGlobalError('O arquivo deve ter no máximo 5MB.');
      return;
    }
    setSelectedAvatarFile(file);
    const url = URL.createObjectURL(file);
    setPreviewAvatarUrl(url);
  };

  const handleBannerFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      setSelectedBannerFile(null);
      if (previewBannerUrl) {
        URL.revokeObjectURL(previewBannerUrl);
        setPreviewBannerUrl(null);
      }
      return;
    }
    if (!file.type.startsWith('image/')) {
      dispatchGlobalError('Apenas arquivos de imagem são permitidos.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      dispatchGlobalError('O arquivo deve ter no máximo 5MB.');
      return;
    }
    setSelectedBannerFile(file);
    const url = URL.createObjectURL(file);
    setPreviewBannerUrl(url);
  };

  const handleAvatarUpload = async () => {
    if (!id || !selectedAvatarFile) return;
    setIsUploadingAvatar(true);
    try {
      const imageUrl = await uploadImagemLiga(id, selectedAvatarFile);
      await atualizarImagemLiga(id, imageUrl);

      setValue('imagemUrl', imageUrl);
      setSelectedAvatarFile(null);
      if (previewAvatarUrl) {
        URL.revokeObjectURL(previewAvatarUrl);
        setPreviewAvatarUrl(null);
      }

      await refetch();

      setAvatarUploaded(true);
      setSnackbar({ open: true, message: 'Avatar atualizado com sucesso.', severity: 'success' });
    } catch (err) {
      // Erro já tratado no service
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleBannerUpload = async () => {
    if (!id || !selectedBannerFile) return;
    setIsUploadingBanner(true);
    try {
      const bannerUrl = await uploadBannerLiga(id, selectedBannerFile);
      await atualizarBannerLiga(id, bannerUrl);

      setValue('bannerUrl', bannerUrl);
      setSelectedBannerFile(null);
      if (previewBannerUrl) {
        URL.revokeObjectURL(previewBannerUrl);
        setPreviewBannerUrl(null);
      }

      await refetch();

      setBannerUploaded(true);
      setSnackbar({ open: true, message: 'Banner atualizado com sucesso.', severity: 'success' });
    } catch (err) {
      // Erro já tratado no service
    } finally {
      setIsUploadingBanner(false);
    }
  };

  const onSubmit = async (formData: AdminEditLeagueFormValues) => {
    if (!id) return;
    if (!selectedUser) {
      setErrorMsg('Organizador é obrigatório.');
      return;
    }

    setSaving(true);
    setErrorMsg('');

    try {
      const input: LigaInput = {
        nome: formData.nome.trim(),
        username: formData.username.trim(),
        enderecoCompleto: formData.enderecoCompleto?.trim() || null,
        ramoAtividade: formData.ramoAtividade?.trim() || null,
      };

      if (!avatarUploaded) {
        input.imagemUrl = formData.imagemUrl || null;
      }
      if (!bannerUploaded) {
        input.bannerUrl = formData.bannerUrl || null;
      }

      await atualizarLiga(id, input);

      // Atualizar organizador se o usuário mudou
      if (currentOrganizer && selectedUser && currentOrganizer.usuario?.id !== selectedUser.id) {
        await atualizarOrganizador({
          id: currentOrganizer.id,
          input: {
            ligaId: id,
            usuarioId: selectedUser.id,
            nome: selectedUser.nome,
            nomeFuncao: 'ADMINISTRADOR',
          },
        });
      }

      setSnackbar({ open: true, message: 'Liga atualizada com sucesso.', severity: 'success' });

      setTimeout(() => {
        navigate('/admin/ligas');
      }, 1500);
    } catch (err) {
      setErrorMsg('Não foi possível salvar as alterações. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/admin/ligas');
  };

  if (loading) {
    return (
      <div className="main-wrapper">
        <Header />
        <div className="main-content theme-dark-bg right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="middle-wrap">
                <Load />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !liga) {
    return (
      <div className="main-wrapper">
        <Header />
        <div className="main-content theme-dark-bg right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="middle-wrap">
                <div className="card w-100 border-0 bg-white shadow-xs p-0 mb-4">
                  <div className="card-body p-4">
                    <div className="alert alert-danger" role="alert">
                      {error ? 'Erro ao carregar informações da liga.' : 'Liga não encontrada.'}
                    </div>
                    <button className="btn btn-primary mt-3" onClick={handleBack}>
                      Voltar para Ligas
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const watchedImagemUrl = watch('imagemUrl');
  const watchedBannerUrl = watch('bannerUrl');
  const displayAvatarUrl = previewAvatarUrl || watchedImagemUrl || '/assets/images/user.png';
  const displayBannerUrl = previewBannerUrl || watchedBannerUrl || '/assets/images/comingsoon.svg';

  return (
    <div className="main-wrapper">
      <Header />
      <div className="main-content theme-dark-bg right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left">
            <div className="middle-wrap" style={{ maxWidth: '1200px' }}>
              <div className="container py-3">
                <div className="mb-3">
                  <PageBreadcrumb
                    items={[
                      { label: 'Ligas', href: '/admin/ligas' },
                      { label: liga?.nome ?? 'Liga' },
                    ]}
                    onBack={handleBack}
                    variant="bootstrap"
                  />
                </div>
              </div>
              <div className="card w-100 border-0 bg-white shadow-xs p-0 mb-4">
                <div className="card-body p-4 w-100 bg-current border-0 d-flex rounded-3 align-items-center">
                  <h4 className="font-xs text-white fw-600 mb-0 mt-2 flex-grow-1">
                    Editar Liga
                  </h4>
                </div>
              </div>

              <div className="card w-100 border-0 bg-white shadow-xs p-0 mb-4">
                <div className="card-body p-4">
                  {errorMsg && (
                    <div className="alert alert-danger" role="alert">
                      {errorMsg}
                    </div>
                  )}

                  {/* Tabs Navigation */}
                  <ul className="nav nav-tabs mb-4" role="tablist">
                    <li className="nav-item" role="presentation">
                      <button
                        className={`nav-link ${activeTab === 'informacoes' ? 'active' : ''}`}
                        type="button"
                        onClick={() => setActiveTab('informacoes')}
                        role="tab"
                        aria-selected={activeTab === 'informacoes'}
                      >
                        Informações
                      </button>
                    </li>
                    <li className="nav-item" role="presentation">
                      <button
                        className={`nav-link ${activeTab === 'organizadores' ? 'active' : ''}`}
                        type="button"
                        onClick={() => setActiveTab('organizadores')}
                        role="tab"
                        aria-selected={activeTab === 'organizadores'}
                      >
                        Organizadores
                      </button>
                    </li>
                    <li className="nav-item" role="presentation">
                      <button
                        className={`nav-link ${activeTab === 'configPagamentos' ? 'active' : ''}`}
                        type="button"
                        onClick={() => setActiveTab('configPagamentos')}
                        role="tab"
                        aria-selected={activeTab === 'configPagamentos'}
                      >
                        Config Pagamentos
                      </button>
                    </li>
                    <li className="nav-item" role="presentation">
                      <button
                        className={`nav-link ${activeTab === 'pagamentos' ? 'active' : ''}`}
                        type="button"
                        onClick={() => setActiveTab('pagamentos')}
                        role="tab"
                        aria-selected={activeTab === 'pagamentos'}
                      >
                        Pagamentos
                      </button>
                    </li>
                  </ul>

                  {/* Tab Content */}
                  <div className="tab-content">
                    {/* Aba Informações */}
                    <div
                      className={`tab-pane fade ${activeTab === 'informacoes' ? 'show active' : ''}`}
                      role="tabpanel"
                    >
                      <form onSubmit={handleFormSubmit(onSubmit)}>
                        {/* Banner */}
                        <div className="mb-4">
                          <label className="form-label fw-600">Banner</label>
                          <div className="position-relative mb-3">
                            <div
                              className="bg-image-cover bg-image-center rounded-3"
                              style={{
                                height: '200px',
                                backgroundImage: `url("${displayBannerUrl}")`,
                                backgroundColor: '#f0f0f0',
                              }}
                            >
                              {selectedBannerFile && (
                                <div className="position-absolute top-0 end-0 p-2">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-primary"
                                    onClick={handleBannerUpload}
                                    disabled={isUploadingBanner}
                                    aria-label="Salvar banner da liga"
                                  >
                                    {isUploadingBanner ? 'Enviando...' : 'Salvar Banner'}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                          <input
                            type="file"
                            className="form-control"
                            accept="image/*"
                            onChange={handleBannerFileChange}
                            disabled={isUploadingBanner}
                          />
                          <small className="text-muted">Tamanho máximo: 5MB. Formatos: JPG, PNG, GIF</small>
                        </div>

                        {/* Avatar */}
                        <div className="mb-4">
                          <label className="form-label fw-600">Avatar</label>
                          <div className="d-flex align-items-center gap-3 mb-3">
                            <div
                              className="rounded-circle overflow-hidden"
                              style={{ width: '120px', height: '120px', border: '2px solid #ddd' }}
                            >
                              <img
                                src={displayAvatarUrl}
                                alt="Avatar"
                                className="w-100 h-100"
                                style={{ objectFit: 'cover' }}
                              />
                            </div>
                            {selectedAvatarFile && (
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={handleAvatarUpload}
                                disabled={isUploadingAvatar}
                                aria-label="Salvar avatar da liga"
                              >
                                {isUploadingAvatar ? 'Enviando...' : 'Salvar Avatar'}
                              </button>
                            )}
                          </div>
                          <input
                            type="file"
                            className="form-control"
                            accept="image/*"
                            onChange={handleAvatarFileChange}
                            disabled={isUploadingAvatar}
                          />
                          <small className="text-muted">Tamanho máximo: 5MB. Formatos: JPG, PNG, GIF</small>
                        </div>

                        {/* Nome */}
                        <div className="mb-3">
                          <label htmlFor="nome" className="form-label fw-600">
                            Nome <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control ${formErrors.nome ? 'is-invalid' : ''}`}
                            id="nome"
                            {...register('nome')}
                            disabled={saving}
                          />
                          {formErrors.nome && (
                            <div className="invalid-feedback">{formErrors.nome.message}</div>
                          )}
                        </div>

                        {/* Username */}
                        <div className="mb-4">
                          <label htmlFor="username" className="form-label fw-600">
                            Username <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control ${formErrors.username ? 'is-invalid' : ''}`}
                            id="username"
                            {...register('username')}
                            disabled={saving}
                            placeholder="@username"
                          />
                          {formErrors.username ? (
                            <div className="invalid-feedback">{formErrors.username.message}</div>
                          ) : (
                            <small className="text-muted">Pode conter apenas letras, números, underscore (_) e hífen (-)</small>
                          )}
                        </div>

                        {/* Endereço completo da empresa */}
                        <div className="mb-3">
                          <label htmlFor="enderecoCompleto" className="form-label fw-600">
                            Endereço completo da empresa
                          </label>
                          <textarea
                            className="form-control"
                            id="enderecoCompleto"
                            {...register('enderecoCompleto')}
                            disabled={saving}
                            rows={2}
                            placeholder="Rua, número, complemento, bairro, cidade/UF, CEP"
                          />
                        </div>

                        {/* Ramo / Atividade da empresa */}
                        <div className="mb-4">
                          <label htmlFor="ramoAtividade" className="form-label fw-600">
                            Ramo / Atividade da empresa
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            id="ramoAtividade"
                            {...register('ramoAtividade')}
                            disabled={saving}
                            placeholder="Ex.: Organização de eventos esportivos"
                          />
                        </div>

                        {/* Organizador */}
                        <div className="mb-4">
                          <Autocomplete
                            options={userOptions}
                            getOptionLabel={(option) => `${option.nome} (${option.email})`}
                            value={selectedUser}
                            onChange={(_, newValue) => setSelectedUser(newValue)}
                            inputValue={userInputValue}
                            onInputChange={(_, newInputValue) => setUserInputValue(newInputValue)}
                            loading={userLoading}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Organizador"
                                required
                                helperText="Selecione o usuário que será o administrador da liga."
                                disabled={saving}
                              />
                            )}
                            noOptionsText={userInputValue ? 'Nenhum usuário encontrado' : 'Digite para buscar usuários'}
                            disabled={saving}
                          />
                        </div>

                        {/* Botões */}
                        <div className="d-flex justify-content-end gap-2">
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleBack}
                            disabled={saving}
                          >
                            Cancelar
                          </button>
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={saving || isUploadingAvatar || isUploadingBanner || !selectedUser}
                          >
                            {saving ? 'Salvando...' : 'Salvar'}
                          </button>
                        </div>
                      </form>
                    </div>

                    {/* Aba Organizadores */}
                    <div
                      className={`tab-pane fade ${activeTab === 'organizadores' ? 'show active' : ''}`}
                      role="tabpanel"
                    >
                      {id && (
                        <LeagueOrganizersTab
                          ligaId={id}
                          canEditLiga={true}
                        />
                      )}
                    </div>

                    {/* Aba Config Pagamentos */}
                    <div
                      className={`tab-pane fade ${activeTab === 'configPagamentos' ? 'show active' : ''}`}
                      role="tabpanel"
                    >
                      {id && <ConfigPagamentosTab ligaId={id} />}
                    </div>

                    {/* Aba Pagamentos */}
                    <div
                      className={`tab-pane fade ${activeTab === 'pagamentos' ? 'show active' : ''}`}
                      role="tabpanel"
                    >
                      {id && <AdminLigaPaymentsTab ligaId={id} />}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AdminEditLeague;
